import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { ShieldAlert } from "lucide-react";

/**
 * PermissionGate — wraps a route/component and enforces a permission key
 * (or any-of a list). Users without the permission are redirected to
 * `/dashboard` (default). Super-admins always pass.
 *
 * Usage:
 *   <PermissionGate perm="reports:view"><SalesReport /></PermissionGate>
 *   <PermissionGate anyOf={["expenses:view","reports:view"]}>{...}</PermissionGate>
 */
export function PermissionGate({ perm, anyOf, redirectTo = "/dashboard", children }) {
  const { hasPermission, session } = useAuth();
  if (!session) return null;
  const ok = perm
    ? hasPermission(perm)
    : anyOf
    ? anyOf.some((p) => hasPermission(p))
    : true;
  if (ok) return children;
  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }
  return (
    <div className="max-w-lg mx-auto mt-16" data-testid="permission-denied">
      <Card className="border-border">
        <CardContent className="p-8 text-center space-y-3">
          <ShieldAlert className="h-10 w-10 text-amber-600 mx-auto" />
          <h2 className="text-lg font-semibold">Sin permiso</h2>
          <p className="text-sm text-muted-foreground">
            Tu rol no tiene acceso a esta sección. Contacta a gerencia si necesitas el permiso.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
